import React from 'react';
import { ArrowLeft, Paperclip, Send } from 'lucide-react';
import { centerToast } from './CenterToast';

// Full-screen chat drawer — used both from an individual card's "Chat" button
// and from the global "All chats" inbox. It overrides everything below it
// (including the bottom nav / card action bar) while open.
export default function ChatDrawer({ isOpen, title, subtitle, messages = [], draft, onDraftChange, onSend, onBack }) {
  return (
    <>
      {/* Dims whatever sits beside the drawer. Never seen on a handset, where the
          drawer covers the screen; in a desktop browser it frames the column. */}
      <div
        aria-hidden="true"
        onClick={onBack}
        className={`fixed inset-0 z-[69] bg-black/30 transition-opacity duration-300 ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
      />
      {/* Slides inside the app's own column so the closed drawer is clipped away
          rather than parked beside the phone on a wide screen. */}
      <div className="fixed inset-0 z-[70] pointer-events-none">
      <div className="relative mx-auto h-full max-w-[480px] overflow-hidden">
      <div
        data-testid="chat-drawer"
        role="dialog"
        aria-modal="true"
        aria-label={`Chat with ${title}`}
        className={`absolute inset-0 bg-white flex flex-col transition-transform duration-300 ease-out ${
          isOpen ? 'translate-x-0 pointer-events-auto' : 'translate-x-full pointer-events-none'
        }`}
      >
      <div className="flex items-center gap-2.5 px-2 pt-4 pb-3 border-b border-border/60 flex-shrink-0">
        <button
          type="button"
          data-testid="chat-drawer-back"
          onClick={onBack}
          aria-label="Back"
          className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-muted flex-shrink-0"
        >
          <ArrowLeft className="h-5 w-5 text-[#0F172A]" />
        </button>
        <div className="w-9 h-9 rounded-full bg-primary text-white flex items-center justify-center text-[11px] font-bold flex-shrink-0">
          IO
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-[#0F172A] truncate">{title}</p>
          {subtitle && <p className="text-[11px] text-muted-foreground truncate">{subtitle}</p>}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4 flex flex-col gap-3" data-testid="chat-message-list">
        {messages.length === 0 && (
          <p className="text-xs text-muted-foreground text-center py-6">No messages yet. Say hi to the organiser!</p>
        )}
        {messages.map((m) => (
          <div key={m.id} className={`flex ${m.sender === 'me' ? 'justify-end' : 'justify-start'}`}>
            {m.sender === 'org' && (
              <div className="w-7 h-7 rounded-full bg-primary text-white flex items-center justify-center text-[10px] font-bold flex-shrink-0 mr-2">
                IO
              </div>
            )}
            <div
              className={`max-w-[75%] px-3.5 py-2.5 ${
                m.sender === 'me'
                  ? 'bg-primary text-white rounded-2xl rounded-br-sm'
                  : 'bg-white border border-border rounded-2xl rounded-bl-sm'
              }`}
            >
              {m.sender === 'org' && (
                <p className="text-[10px] font-bold text-primary mb-0.5">{subtitle || 'IhsanOne Team'}</p>
              )}
              <p className={`text-sm leading-snug whitespace-pre-line ${m.sender === 'me' ? 'text-white' : 'text-foreground'}`}>
                {m.text}
              </p>
              <p className={`text-[10px] mt-1 ${m.sender === 'me' ? 'text-white/70 text-right' : 'text-muted-foreground'}`}>
                {m.time}
              </p>
            </div>
          </div>
        ))}
      </div>

      <div className="px-3 py-3 border-t border-border/60 flex items-center gap-2 bg-white flex-shrink-0">
        <button
          type="button"
          data-testid="chat-attach-button"
          onClick={() => centerToast('Attachments — coming soon', '📎')}
          aria-label="Attach"
          className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-muted flex-shrink-0"
        >
          <Paperclip className="h-4.5 w-4.5 text-muted-foreground" />
        </button>
        <input
          data-testid="chat-message-input"
          value={draft}
          onChange={(e) => onDraftChange(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') onSend(); }}
          placeholder="Type a message..."
          className="flex-1 h-10 rounded-full bg-muted/60 px-4 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
        />
        <button
          type="button"
          data-testid="chat-send-button"
          onClick={onSend}
          aria-label="Send"
          className="w-9 h-9 rounded-full bg-primary text-white flex items-center justify-center flex-shrink-0"
        >
          <Send className="h-4 w-4" />
        </button>
      </div>
      </div>
      </div>
      </div>
    </>
  );
}
